import ExternalFilesContext from '@/components/FileAttachments/ExternalFilesContext';
import UploadedImagesContext from '@/components/FileAttachments/UploadedImagesContext';
import {
  type RubricProofLink,
  type DatabaseExternalFile,
  type UploadedImage as UploadedImageType,
} from '@/lib/types';
import sortByCreatedAt from '@/lib/utils/sorting/sortByCreatedAt';
import { useContext } from 'react';
import FilesWrapper from '@/components/FileAttachments/FilesWrapper';
import UploadedImage from '@/components/FileAttachments/UploadedImage';
import ExternalFile from '@/components/FileAttachments/ExternalFile';
import RestrictedToPortfolioMode from '@/components/RestrictedToPortfolioMode';
import PortfolioMode from '@/lib/constants/PortfolioMode.enum';
import AddFiles from '@/components/FileAttachments/AddFiles';
import ProofLink from './ProofLink';
import AddProofLink from './AddProofLink';
import NoSectionContentMessage from '../../NoSectionContentMessage';
import useProofLinks from './useProofLinks';
import { FormattedMessage } from 'react-intl';
import PortfolioModeContext from '@/lib/contexts/PortfolioModeContext';
import styled from '@emotion/styled';

const StyledFilesWrapper = styled(FilesWrapper)({
  marginTop: 0,
});

type ProofFilesAndLinksProps = {
  levelId: number;
};

const ProofFilesAndLinks = ({ levelId }: ProofFilesAndLinksProps) => {
  const { images } = useContext(UploadedImagesContext);
  const { externalFiles } = useContext(ExternalFilesContext);

  const proofLinks = useProofLinks(levelId);
  const filesAndLinks = [...images, ...externalFiles, ...proofLinks].sort(
    sortByCreatedAt,
  ) as (UploadedImageType | DatabaseExternalFile | RubricProofLink)[];

  const portfolioMode = useContext(PortfolioModeContext);

  return (
    <>
      {filesAndLinks.length === 0 && (
        <NoSectionContentMessage>
          <FormattedMessage id="portfolio.level.attachments.proof.no_proof_message" />
        </NoSectionContentMessage>
      )}
      {(filesAndLinks.length > 0 ||
        portfolioMode === PortfolioMode.STUDENT) && (
        <StyledFilesWrapper>
          {filesAndLinks.map(
            (
              fileOrLink:
                UploadedImageType | DatabaseExternalFile | RubricProofLink,
            ) => {
              if ('link' in fileOrLink) {
                return <ProofLink proofLink={fileOrLink} key={fileOrLink.id} />;
              }

              if ('file' in fileOrLink) {
                return (
                  <UploadedImage
                    image={fileOrLink}
                    key={`uploaded-image-${fileOrLink.id}`}
                  />
                );
              }

              return (
                <ExternalFile
                  externalFile={fileOrLink}
                  key={`external-file-${fileOrLink.id}`}
                />
              );
            },
          )}
          <RestrictedToPortfolioMode mode={PortfolioMode.STUDENT}>
            <AddProofLink levelId={levelId} />
            <AddFiles />
          </RestrictedToPortfolioMode>
        </StyledFilesWrapper>
      )}
    </>
  );
};

export default ProofFilesAndLinks;
